import { Add, Delete } from '@mui/icons-material';
import { Box, Typography, Button, IconButton, Paper, Divider, Tabs, Tab } from '@mui/material';
import { Formik, Form, FieldArray } from 'formik';
import React, { useMemo } from 'react';

import FormSkeleton from './formSkeleton';
import StepNote from './stepNote';
import FormField from 'components/common/formikForm/formField';
import { FormBusyProvider } from 'components/common/formikForm/formBusyContext';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import { StepNavigation } from 'components/v2/StepNavigation';

import { changeTabIfValid } from './tabValidation';
import { BooleanValue, PartnerEducationDuration } from 'constant';
import useEnabledCountries from 'hooks/useEnabledCountries';
import languages from 'data/selectInputOptions/languageList.json';
import {
    useCountries,
    useEducationLevels,
    useRegions,
    useFieldOfStudyByCountryCode,
    useUkInstitutions,
    useCaInstitutions,
    useAuInstitutions,
    useEducation,
    useSpouseEducation,
    useSaveEducation,
    useSaveSpouseEducation,
} from 'hooks';
import useTranslate from 'hooks/useTranslate';
import type { Education, PartnerEducationInterface } from 'types';
import {
    formatDayjsDate,
    generateOptionFromObject,
    generateOptionsFromString,
    generateSelectOptions,
} from 'utils/dataFormatter';
import {
    chosenCountryName,
    countryNameOptions,
    enabledCountryCodeForRow,
    enabledMarketFields,
    fieldOfStudyMarketVisible,
    isEducationRegionCountry,
    rowCountryEnabled,
} from 'utils/countries';
import { createEducationValidationSchema } from 'utils/validations/educationSchema';

const booleanOptions = generateOptionFromObject<boolean>(BooleanValue);

const languageOptions = generateOptionsFromString(
    Object.values(languages).map((item) => item.name)
);

const emptyEducation: Partial<Education> = {
    completed: false,
    country: '',
    current: false,
    end: '',
    field_of_study_au: null,
    field_of_study_au_en: null,
    field_of_study_au_id: null,
    field_of_study_ca: null,
    field_of_study_ca_en: null,
    field_of_study_ca_id: null,
    field_of_study_uk: null,
    field_of_study_uk_en: null,
    field_of_study_uk_id: null,
    grade_average: null,
    institution_name: '',
    language: '',
    region: '',
    spouse_id: null,
    start: '',
    type: '',
    zip: '',
};

const emptySpouseEducation: Partial<PartnerEducationInterface> = {
    current: false,
    duration: null,
    country: '',
    region: '',
    completed: true,
    type: '',
    start: '',
    end: '',
};

interface EducationStepProps {
    isLastStep: boolean;
    hasSpouse: string;
    onNext: (data: Record<string, unknown>) => void;
    onBack: () => void;
}

const EducationStep = ({
    hasSpouse: hasSpouseValue,
    onNext,
    onBack,
    isLastStep,
}: EducationStepProps) => {
    const { translate } = useTranslate();
    const { codes, hasSelfServeMarket } = useEnabledCountries();
    const validationSchema = useMemo(() => createEducationValidationSchema(codes), [codes]);
    const hasSpouse = hasSpouseValue === 'Yes';
    const [activeTab, setActiveTab] = React.useState(0);

    const { data: educationData, isLoading } = useEducation();
    const { data: spouseEducationData, isLoading: spouseEducationLoading } =
        useSpouseEducation(hasSpouse);
    const saveEducation = useSaveEducation();
    const saveSpouseEducation = useSaveSpouseEducation();
    const { data: countries = [] } = useCountries();
    const { data: educationLevels = [] } = useEducationLevels();
    const { data: regions } = useRegions();

    const countryOptions = generateOptionsFromString(countries ?? []);
    const spouseCountryOptions = (country?: string | null) =>
        generateOptionsFromString(countryNameOptions(codes, country));
    const soleSpouseCountry = chosenCountryName(codes) ?? '';
    const partnerEducationDurationOptions = generateOptionsFromString(PartnerEducationDuration);
    const educationLevelOptions = generateOptionsFromString(educationLevels ?? []);

    const initialValues = {
        education:
            educationData && educationData.length > 0
                ? educationData
                : ([emptyEducation] as Education[]),
        spouse_education:
            spouseEducationData && spouseEducationData.length > 0
                ? spouseEducationData.map((row) => ({
                      ...row,
                      country: row.country || soleSpouseCountry,
                  }))
                : [],
    };

    const handleSubmit = async (values: {
        education: Education[];
        spouse_education: Education[];
    }) => {
        try {
            const education = values.education;
            await saveEducation.mutateAsync(education);

            let spouseEducation: Partial<PartnerEducationInterface>[] = [];
            if (hasSpouse && values.spouse_education.length > 0) {
                spouseEducation = values.spouse_education;
                await saveSpouseEducation.mutateAsync(spouseEducation);
            }

            onNext({
                education: education,
                spouse_education: hasSpouse ? spouseEducation : [],
            });
        } catch {
            // Error alert is handled by the mutation hook onError
        }
    };

    if (isLoading || (hasSpouse && spouseEducationLoading)) {
        return <FormSkeleton fields={8} />;
    }

    return (
        <Box>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, isValid, isSubmitting, setFieldValue, validateForm, setTouched }) => {
                    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
                        void changeTabIfValid({
                            nextTab: newValue,
                            activeTab,
                            tabFieldKeys: [['education'], ['spouse_education']],
                            values,
                            validateForm,
                            setTouched,
                            setActiveTab,
                        });
                    };

                    const isSaving =
                        isSubmitting || saveEducation.isPending || saveSpouseEducation.isPending;

                    return (
                        <FormBusyProvider busy={isSaving}>
                            <Form>
                                {hasSpouse && (
                                    <Tabs
                                        value={activeTab}
                                        onChange={handleTabChange}
                                        sx={{ mb: 3 }}
                                    >
                                        <Tab label="Your Education" />
                                        <Tab label="Spouse Education" />
                                    </Tabs>
                                )}

                                {/* User Education Tab */}
                                {(!hasSpouse || activeTab === 0) && (
                                    <FieldArray name="education">
                                        {({ push, remove }) => (
                                            <Box>
                                                <StepNote messageKey="educationNote" />
                                                {values.education.map((edu, index) => {
                                                    // AssessmentV2: region for AU/CA/UK (+ FR labels)
                                                    const showRegion = isEducationRegionCountry(
                                                        edu.country
                                                    );
                                                    const regionOptions = generateOptionsFromString(
                                                        showRegion && edu.country
                                                            ? (
                                                                  regions as Record<
                                                                      string,
                                                                      string[]
                                                                  > | null
                                                              )?.[edu.country] || []
                                                            : []
                                                    );
                                                    const showLanguage = hasSelfServeMarket;
                                                    const showInstitution =
                                                        enabledCountryCodeForRow(
                                                            codes,
                                                            edu.country
                                                        ) !== null;

                                                    return (
                                                        <Paper
                                                            key={index}
                                                            elevation={1}
                                                            sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    mb: 2,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="h6"
                                                                    sx={{ fontSize: '1.1rem' }}
                                                                >
                                                                    Education {index + 1}
                                                                </Typography>
                                                                {values.education.length > 1 && (
                                                                    <IconButton
                                                                        onClick={() =>
                                                                            remove(index)
                                                                        }
                                                                        color="error"
                                                                        size="small"
                                                                    >
                                                                        <Delete />
                                                                    </IconButton>
                                                                )}
                                                            </Box>
                                                            <Divider sx={{ mb: 2 }} />

                                                            <FormGridLayout>
                                                                <FormField
                                                                    type="autosuggest"
                                                                    name={`education.${index}.type`}
                                                                    label="type"
                                                                    options={educationLevelOptions}
                                                                    onChange={(value: unknown) => {
                                                                        setFieldValue(
                                                                            `education.${index}.type`,
                                                                            value
                                                                        );
                                                                        // High school clears all FOS keys (AssessmentV2)
                                                                        if (
                                                                            value === 'High school'
                                                                        ) {
                                                                            (
                                                                                [
                                                                                    'field_of_study_au_id',
                                                                                    'field_of_study_ca_id',
                                                                                    'field_of_study_uk_id',
                                                                                    'field_of_study_au',
                                                                                    'field_of_study_ca',
                                                                                    'field_of_study_uk',
                                                                                    'field_of_study_au_en',
                                                                                    'field_of_study_ca_en',
                                                                                    'field_of_study_uk_en',
                                                                                ] as const
                                                                            ).forEach((key) =>
                                                                                setFieldValue(
                                                                                    `education.${index}.${key}`,
                                                                                    key.endsWith(
                                                                                        '_id'
                                                                                    )
                                                                                        ? null
                                                                                        : null
                                                                                )
                                                                            );
                                                                        }
                                                                    }}
                                                                />

                                                                {enabledMarketFields(codes).map(
                                                                    (market) =>
                                                                        fieldOfStudyMarketVisible(
                                                                            codes,
                                                                            edu.type,
                                                                            market.code
                                                                        ) ? (
                                                                            <FieldOfStudy
                                                                                key={market.code}
                                                                                index={index}
                                                                                countryCode={
                                                                                    market.code
                                                                                }
                                                                                idKey={
                                                                                    market.fieldOfStudyIdKey
                                                                                }
                                                                                nameKey={
                                                                                    market.fieldOfStudyNameKey
                                                                                }
                                                                                nameEnKey={
                                                                                    market.fieldOfStudyNameEnKey
                                                                                }
                                                                                labelKey={
                                                                                    market.fieldOfStudyLabelKey
                                                                                }
                                                                                setFieldValue={
                                                                                    setFieldValue
                                                                                }
                                                                            />
                                                                        ) : null
                                                                )}

                                                                <FormField
                                                                    type="autosuggest"
                                                                    name={`education.${index}.country`}
                                                                    label="country"
                                                                    options={countryOptions}
                                                                    onChange={(value) => {
                                                                        setFieldValue(
                                                                            `education.${index}.country`,
                                                                            value
                                                                        );
                                                                        setFieldValue(
                                                                            `education.${index}.region`,
                                                                            ''
                                                                        );
                                                                    }}
                                                                />
                                                                {showRegion && (
                                                                    <FormField
                                                                        type="autosuggest"
                                                                        name={`education.${index}.region`}
                                                                        label="region"
                                                                        options={regionOptions}
                                                                        disabled={!edu.country}
                                                                    />
                                                                )}
                                                                {rowCountryEnabled(
                                                                    codes,
                                                                    edu.country,
                                                                    'AU'
                                                                ) && (
                                                                    <FormField
                                                                        name={`education.${index}.zip`}
                                                                        label="zipLabel"
                                                                        tooltip="zipTooltipEducation"
                                                                    />
                                                                )}
                                                                {showLanguage && (
                                                                    <FormField
                                                                        type="autosuggest"
                                                                        name={`education.${index}.language`}
                                                                        label="language"
                                                                        options={languageOptions}
                                                                    />
                                                                )}
                                                                {showInstitution && (
                                                                    <InstitutionField
                                                                        index={index}
                                                                        country={edu.country}
                                                                        enabled={codes}
                                                                    />
                                                                )}
                                                                <FormField
                                                                    type="monthyear"
                                                                    name={`education.${index}.start`}
                                                                    label="startDate"
                                                                />
                                                                <FormField
                                                                    type="monthyear"
                                                                    name={`education.${index}.end`}
                                                                    label="endDate"
                                                                    minDate={formatDayjsDate(
                                                                        edu.start
                                                                    )}
                                                                    disabled={
                                                                        !edu.start || !!edu.current
                                                                    }
                                                                />
                                                                <FormField
                                                                    type="radio"
                                                                    row
                                                                    name={`education.${index}.current`}
                                                                    label="ongoing"
                                                                    options={booleanOptions}
                                                                    onChange={(value: unknown) => {
                                                                        const boolValue =
                                                                            value === 'true' ||
                                                                            value === true;
                                                                        setFieldValue(
                                                                            `education.${index}.current`,
                                                                            boolValue
                                                                        );
                                                                        if (boolValue) {
                                                                            setFieldValue(
                                                                                `education.${index}.completed`,
                                                                                false
                                                                            );
                                                                            setFieldValue(
                                                                                `education.${index}.end`,
                                                                                ''
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                                <FormField
                                                                    type="radio"
                                                                    row
                                                                    name={`education.${index}.completed`}
                                                                    label="completed"
                                                                    options={booleanOptions}
                                                                    onChange={(value: unknown) => {
                                                                        const boolValue =
                                                                            value === 'true' ||
                                                                            value === true;
                                                                        setFieldValue(
                                                                            `education.${index}.completed`,
                                                                            boolValue
                                                                        );
                                                                        if (boolValue) {
                                                                            setFieldValue(
                                                                                `education.${index}.current`,
                                                                                false
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            </FormGridLayout>
                                                        </Paper>
                                                    );
                                                })}

                                                <Button
                                                    variant="outlined"
                                                    startIcon={<Add />}
                                                    onClick={() => push(emptyEducation)}
                                                    fullWidth
                                                    sx={{ mb: 3 }}
                                                >
                                                    {translate('addEducation')}
                                                </Button>
                                            </Box>
                                        )}
                                    </FieldArray>
                                )}

                                {/* Spouse Education Tab */}
                                {hasSpouse && activeTab === 1 && (
                                    <FieldArray name="spouse_education">
                                        {({ push, remove }) => (
                                            <Box>
                                                {values.spouse_education.map(
                                                    (edu: Education, index: number) => {
                                                        const showSpouseRegion =
                                                            isEducationRegionCountry(edu.country);
                                                        const regionOptions =
                                                            generateOptionsFromString(
                                                                showSpouseRegion && edu.country
                                                                    ? (
                                                                          regions as Record<
                                                                              string,
                                                                              string[]
                                                                          > | null
                                                                      )?.[edu.country] || []
                                                                    : []
                                                            );
                                                        return (
                                                            <Paper
                                                                key={index}
                                                                elevation={1}
                                                                sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        justifyContent:
                                                                            'space-between',
                                                                        alignItems: 'center',
                                                                        mb: 2,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle1"
                                                                        sx={{ fontSize: '1.05rem' }}
                                                                    >
                                                                        Spouse Education {index + 1}
                                                                    </Typography>
                                                                    <IconButton
                                                                        onClick={() =>
                                                                            remove(index)
                                                                        }
                                                                        color="error"
                                                                        size="small"
                                                                    >
                                                                        <Delete />
                                                                    </IconButton>
                                                                </Box>
                                                                <Divider sx={{ mb: 2 }} />

                                                                <FormGridLayout>
                                                                    <FormField
                                                                        type="autosuggest"
                                                                        name={`spouse_education.${index}.country`}
                                                                        label="country"
                                                                        options={spouseCountryOptions(
                                                                            values.spouse_education[
                                                                                index
                                                                            ]?.country
                                                                        )}
                                                                        onChange={(value) => {
                                                                            setFieldValue(
                                                                                `spouse_education.${index}.country`,
                                                                                value
                                                                            );
                                                                            setFieldValue(
                                                                                `spouse_education.${index}.region`,
                                                                                ''
                                                                            );
                                                                        }}
                                                                    />
                                                                    {showSpouseRegion && (
                                                                        <FormField
                                                                            type="autosuggest"
                                                                            name={`spouse_education.${index}.region`}
                                                                            label="region"
                                                                            options={regionOptions}
                                                                            disabled={!edu.country}
                                                                        />
                                                                    )}
                                                                    <FormField
                                                                        type="autosuggest"
                                                                        name={`spouse_education.${index}.duration`}
                                                                        label="programDurationInYears"
                                                                        options={
                                                                            partnerEducationDurationOptions
                                                                        }
                                                                        disabled={!edu.country}
                                                                    />
                                                                    <FormField
                                                                        type="autosuggest"
                                                                        name={`spouse_education.${index}.type`}
                                                                        label="type"
                                                                        options={
                                                                            educationLevelOptions
                                                                        }
                                                                    />
                                                                    <FormField
                                                                        type="monthyear"
                                                                        name={`spouse_education.${index}.start`}
                                                                        label="startDate"
                                                                    />
                                                                    <FormField
                                                                        type="monthyear"
                                                                        name={`spouse_education.${index}.end`}
                                                                        label="endDate"
                                                                        minDate={formatDayjsDate(
                                                                            edu.start
                                                                        )}
                                                                        disabled={
                                                                            !edu.start ||
                                                                            !!edu.current
                                                                        }
                                                                    />
                                                                    <FormField
                                                                        type="radio"
                                                                        row
                                                                        name={`spouse_education.${index}.current`}
                                                                        label="ongoing"
                                                                        options={booleanOptions}
                                                                        onChange={(
                                                                            value: unknown
                                                                        ) => {
                                                                            const boolValue =
                                                                                value === 'true' ||
                                                                                value === true;
                                                                            setFieldValue(
                                                                                `spouse_education.${index}.current`,
                                                                                boolValue
                                                                            );
                                                                            if (boolValue) {
                                                                                setFieldValue(
                                                                                    `spouse_education.${index}.completed`,
                                                                                    false
                                                                                );
                                                                                setFieldValue(
                                                                                    `spouse_education.${index}.end`,
                                                                                    ''
                                                                                );
                                                                            }
                                                                        }}
                                                                    />
                                                                    <FormField
                                                                        type="radio"
                                                                        row
                                                                        name={`spouse_education.${index}.completed`}
                                                                        label="completed"
                                                                        options={booleanOptions}
                                                                        onChange={(
                                                                            value: unknown
                                                                        ) => {
                                                                            const boolValue =
                                                                                value === 'true' ||
                                                                                value === true;
                                                                            setFieldValue(
                                                                                `spouse_education.${index}.completed`,
                                                                                boolValue
                                                                            );
                                                                            if (boolValue) {
                                                                                setFieldValue(
                                                                                    `spouse_education.${index}.current`,
                                                                                    false
                                                                                );
                                                                            }
                                                                        }}
                                                                    />
                                                                </FormGridLayout>
                                                            </Paper>
                                                        );
                                                    }
                                                )}

                                                <Button
                                                    variant="outlined"
                                                    startIcon={<Add />}
                                                    onClick={() =>
                                                        push({
                                                            ...emptySpouseEducation,
                                                            country: soleSpouseCountry,
                                                        })
                                                    }
                                                    fullWidth
                                                    sx={{ mb: 3 }}
                                                >
                                                    Add Spouse Education
                                                </Button>
                                            </Box>
                                        )}
                                    </FieldArray>
                                )}

                                <StepNavigation
                                    isLastStep={isLastStep}
                                    onBack={onBack}
                                    isSubmitting={isSaving}
                                    disableNext={!isValid}
                                />
                            </Form>
                        </FormBusyProvider>
                    );
                }}
            </Formik>
        </Box>
    );
};

export default EducationStep;

/** Institution list for the row's enabled market. Visibility is decided by the parent. */
const InstitutionField = ({
    index,
    country,
    enabled,
}: {
    index: number;
    country?: string | null;
    enabled: readonly string[];
}) => {
    const code = enabledCountryCodeForRow(enabled, country);
    const { data: ukInstitutions = [] } = useUkInstitutions(code === 'UK');
    const { data: caInstitutions = [] } = useCaInstitutions(code === 'CA');
    const { data: auInstitutions = [] } = useAuInstitutions(code === 'AU');
    const institutions =
        code === 'UK' ? ukInstitutions : code === 'CA' ? caInstitutions : auInstitutions;
    const options = generateOptionsFromString(institutions || []);

    return (
        <FormField
            type="autosuggest"
            name={`education.${index}.institution_name`}
            label="institution"
            options={options}
        />
    );
};

/**
 * One field-of-study market (AU / CA / UK).
 * Visibility is decided by the parent from the enabled markets.
 */
const FieldOfStudy = ({
    index,
    countryCode,
    idKey,
    nameKey,
    nameEnKey,
    labelKey,
    setFieldValue,
}: {
    index: number;
    countryCode: 'AU' | 'CA' | 'UK';
    idKey: 'field_of_study_au_id' | 'field_of_study_ca_id' | 'field_of_study_uk_id';
    nameKey: 'field_of_study_au' | 'field_of_study_ca' | 'field_of_study_uk';
    nameEnKey: 'field_of_study_au_en' | 'field_of_study_ca_en' | 'field_of_study_uk_en';
    labelKey: string;
    setFieldValue: (field: string, value: unknown) => void;
}) => {
    const { isFetching, data = [] } = useFieldOfStudyByCountryCode(countryCode);
    const options = generateSelectOptions(data ?? [], 'name', 'id').map((opt) => ({
        label: opt.label,
        value: opt.value as string | number,
    }));

    const handleChange = (value: unknown) => {
        const selectedId = value === '' || value == null ? null : Number(value);
        const selected = (data ?? []).find((item) => item.id === selectedId);

        setFieldValue(`education.${index}.${idKey}`, selectedId);
        setFieldValue(`education.${index}.${nameKey}`, selected?.name ?? null);
        setFieldValue(`education.${index}.${nameEnKey}`, selected?.name ?? null);
    };

    return (
        <FormField
            required
            type="aiselect"
            kind="fieldOfStudy"
            countryCode={countryCode}
            name={`education.${index}.${idKey}`}
            label={labelKey}
            options={options}
            disabled={isFetching}
            onChange={handleChange}
        />
    );
};
